/**
 * Lieferanten.
 *
 * ===================== GEMESSEN STATT ABGELEITET (Fix-Runde 1 zu Task 5a) ========================
 *
 * Bis zu dieser Runde stand hier "ANNAHME: Pfad `/api/suppliers`, aus dem SSE-Entitaetsnamen
 * abgeleitet". Der Pfad stimmt zufaellig, der Entitaetsname stimmte NICHT: das Backend sendet
 * `"supplier"` in der EINZAHL (`backend/src/http/routes/suppliers.ts:91`), nachgeschlagen bleibt
 * `"suppliers"` nirgends haengen. Ereignisname und Pfad stehen deshalb ab jetzt getrennt und
 * benannt hier - siehe den Kopfkommentar von `bestellungen.ts` fuer die ganze Kette.
 *
 * WAS ES UNTER /api/suppliers WIRKLICH GIBT (`backend/src/http/endpunktMatrix.ts`, mit
 * `backend/src/http/routes/suppliers.ts` abgeglichen; seit 2026-10-01 die Uebersichtsliste dazu,
 * siehe `lieferantenUebersichtLaden` unten):
 *
 *   GET   /api/suppliers                       -> Uebersichtsliste (Kennung, Firma, Zahlungsziele)
 *   GET   /api/suppliers/:kLieferant           -> Stammdaten live aus JTL + supplier_extras
 *   PATCH /api/suppliers/:kLieferant/extras    -> `{ rowVersion, felder }`, Recht manage_suppliers
 *
 * Es gibt KEIN Anlegen, KEIN Loeschen und kein `PUT /api/suppliers/:id`. Die vier
 * entsprechenden Funktionen aus `ressource()` standen bis zu dieser Runde hier und zeigten
 * ausnahmslos ins Leere; keine hatte einen Aufrufer. Sie sind ENTFERNT statt kommentiert: eine
 * exportierte Funktion, die nachweislich keine Route trifft, ist eine Falle fuer den naechsten
 * Aufrufer - und ein Kommentar an ihr hat noch nie jemanden davon abgehalten, sie zu benutzen.
 *
 * Dass die Lieferantenliste im Anfangsbestand fehlt, ist deshalb kein Versehen, sondern der
 * gemessene Stand: `state/Speicher.tsx#bestandLaden` holt sie Datensatz fuer Datensatz ueber die
 * `supplierId` der geladenen Bestellungen (siehe dort).
 *
 * `PATCH .../extras` ist hier bewusst NICHT nachgebaut: es gibt heute keine Flaeche, die
 * Lieferanten-Zusatzfelder schreibt (die Lieferantenverwaltung des Bestands ist toter Code -
 * `renderSuppliers()`, `index.html:13903`, schreibt in ein `#page-suppliers`, das es im Geruest
 * nicht gibt). Wer sie baut, findet die Drahtform in der Zeile darueber - und im Backend, nicht in
 * einer Annahme.
 *
 * ===================== FIX-RUNDE 2: AUCH DIESE ANTWORT IST VERSCHACHTELT ========================
 *
 * Derselbe Fehler wie bei den Bestellungen (Kopfkommentar `bestellungen.ts`, Abschnitt "DIE ANTWORT
 * IST VERSCHACHTELT"), eine Entitaet weiter: `GET /api/suppliers/:kLieferant` antwortet mit
 * `{ kLieferant, jtl, extras }` (`backend/src/domaene/lieferanten.ts#ladeLieferant`, :132-145) - OHNE
 * `id` und mit ZWEI verschachtelten Objekten statt einem. Bis zu dieser Runde reichte
 * `lieferantLaden` das unveraendert durch.
 *
 * ZWEI Folgen, beide am Quelltext belegt:
 *
 *   1. `state/reduzierer.ts#listeErsetzen` (:158-166) sucht den vorhandenen Eintrag ueber
 *      `idVon` (:147-151), das eine STRING-Eigenschaft `id` verlangt. Ohne `id` liefert
 *      `findIndex` immer `-1` - der Zweig *anhaengen* greift statt *ersetzen*. Jedes `supplier`-
 *      Ereignis haengte dadurch einen weiteren Eintrag an, unbegrenzt. Vor der Reparatur der
 *      Ereignisnamen (Fix-Runde 1) blieb das folgenlos: `supplier`-Ereignisse liefen in
 *      "Kein Nachlader fuer Entitaet" und erreichten den Reduzierer nie. Seit die Namen stimmen,
 *      kommen sie an - der Speicher waechst seither bei jedem Lieferanten-Update.
 *   2. Die Lieferantenaufloesung traf nirgends: `lib/suppliers.ts#supplierById` (:157-160) sucht
 *      ueber `s.id`, `supplierByName` (:216-222) ueber `s.name` - beides lag verschachtelt
 *      (`id` unter `kLieferant`, der Name unter `jtl.firma`). Betraf auch den Anfangsbestand
 *      (`state/Speicher.tsx#bestandLaden`, Zweig 2): die Aufloesung war dort bereits vor Fix-Runde 1
 *      tot, nur ohne den wachsenden Schaden aus Punkt 1.
 *
 * `entschachtele()` unten loest BEIDE Verschachtelungen an der Naht auf - dieselbe Bauform wie in
 * `bestellungen.ts`, hier mit zwei genesteten Objekten statt einem.
 */
import { anfrage, ressource } from "./basis.ts";
import { entschachtele } from "../lib/lieferantDraht.ts";
import type { Supplier } from "../lib/types.ts";

// `entschachtele` (samt `extrasFlach`) wohnt seit dem KI-Zugang (Stufe 1) in `lib/lieferantDraht.ts`,
// weil der Server dieselbe Umformung fahren muss; die Namen bleiben hier erreichbar. Die Begruendung
// (zwei verschachtelte Objekte, Kollisionsfrage, Namens-Alias) steht dort am Kopf der Funktion.
export { entschachtele };
export { extrasFlach } from "../lib/lieferantDraht.ts";

/**
 * Name dieser Entitaet im SSE-Ereignis (`event.entity`) - EINZAHL. Gemessen an
 * `backend/src/http/routes/suppliers.ts:91`; die Allowlist dazu ist `BROADCAST_ENTITIES` in
 * `backend/src/sse/broadcast.ts`. NICHT der Pfad.
 */
export const SSE_ENTITAET = "supplier";

/**
 * REST-Basispfad - MEHRZAHL. Gemessen an `backend/src/http/endpunktMatrix.ts:105`. Steht als
 * Literal und wird NICHT aus `SSE_ENTITAET` errechnet (Begruendung siehe `bestellungen.ts`).
 */
export const BASISPFAD = "/api/suppliers";

const zugriff = ressource<Supplier>(BASISPFAD);

/**
 * `GET /api/suppliers/:kLieferant`. Die einzige Lesefunktion dieses Moduls, die es im Backend gibt -
 * und zugleich der Nachlader dieser Entitaet (`state/Speicher.tsx`, `NACHLADER`). Die `id` ist die
 * JTL-Kennung als Zeichenkette, genau so, wie sie im SSE-Ereignis steht (`String(kLieferant)`,
 * `suppliers.ts:92`).
 *
 * FIX-RUNDE 2: liest bis hierhin `zugriff.einzeln` unveraendert durch (verschachtelt, ohne `id`) -
 * seit dieser Runde laeuft das Ergebnis durch `entschachtele()`, dieselbe Naht wie
 * `bestellungLaden` in `bestellungen.ts`.
 *
 * Die Uebersichtsliste (`GET /api/suppliers`) steht seit 2026-10-01 in `lieferantenUebersichtLaden`
 * (Dateiende); `lieferantLaden` bleibt der einzige Weg zu EINEM vollen Datensatz.
 */
export async function lieferantLaden(
  id: string,
  signal?: AbortSignal,
  // Nur der SSE-Nachlader setzt das (`AnfrageOptionen.hintergrund`).
  hintergrund?: boolean,
): Promise<Supplier | null> {
  const datensatz = await zugriff.einzeln(id, signal, hintergrund);
  return datensatz === null ? null : entschachtele(datensatz);
}

/** Ein Zahlungsziel (freigegebene Vorlage) eines Lieferanten in der Uebersichtsliste. */
export interface UebersichtZahlungsziel {
  templateId: string;
  label: string;
  isDefault: boolean;
  sortIndex: number;
}

/** Eine Zeile der Lieferanten-Uebersicht, wie `GET /api/suppliers` sie liefert. */
export interface LieferantenUebersichtZeile {
  kLieferant: number;
  firma: string;
  /** `JtlAktiv` ("Y" | "N" | null) roh - `aktivMarke()` ordnet ein. */
  aktiv: unknown;
  shortName: string | null;
  /** Standard zuerst, dann nach `sortIndex` (so liefert es der Server). */
  zahlungsziele: UebersichtZahlungsziel[];
}

/**
 * Die Liste fuer die Uebersichtstabelle: aktive Lieferanten plus stillgelegte mit Zahlungszielen;
 * `alle` liefert jeden. Fehlt `zahlungsziele` an einer Zeile, gilt eine leere Liste - eine
 * unvollstaendige Zeile soll die Tabelle nicht mitreissen.
 */
export async function lieferantenUebersichtLaden(
  optionen: { alle?: boolean; signal?: AbortSignal } = {},
): Promise<LieferantenUebersichtZeile[]> {
  const roh = await anfrage<LieferantenUebersichtZeile[]>(
    optionen.alle === true ? `${BASISPFAD}?alle=1` : BASISPFAD,
    { methode: "GET", signal: optionen.signal },
  );
  if (!Array.isArray(roh)) return [];
  return roh.map((zeile) => ({ ...zeile, shortName: zeile.shortName ?? null, zahlungsziele: zeile.zahlungsziele ?? [] }));
}
